// Doer board — the four-column "Pending Task" layout employees know from the
// AppSheet TaskApp: HT reply pending · HT resolve pending · Delegation · FMS.
// Pure view over the data Overview has already loaded; it fetches nothing.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, ChevronRight, ChevronLeft, AlertTriangle, X, Paperclip } from 'lucide-react';

// Fill the space between the board shell's top bar (56px), bottom bar (64px)
// and the page padding, so the columns use the whole screen.
const COLUMN_HEIGHT = 'max(360px, calc(100vh - 152px))';

const isBlank = (v) => !v || !String(v).trim();

// Keys never worth showing in the "All details" list: internal bookkeeping and
// the per-channel notification flags the sheets carry.
const HIDDEN_DETAIL_KEY = /^(id|_source|rownumber|row)$|whatsapp|telegram|send_|sent_|_link$|^link$/i;

const humanizeKey = (key) =>
  String(key).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// Detail view for any board row. `fields` are the curated headline fields;
// `task` (optional) adds every remaining non-empty column under "All details",
// for sheets whose columns are header-derived and vary.
const DetailModal = ({ detail, onClose }) => {
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const { title, subtitle, badge, body = [], fields = [], attachments = [], task, actions = [], onOpenTab, tabLabel } = detail;
  // Form uploads can hold several links in one cell ("url1, url2").
  const attachmentItems = attachments.flatMap(a =>
    isBlank(a.value) ? [] : (String(a.value).match(/https?:\/\/[^\s,]+/gi) || [String(a.value).trim()]).map((part, i, all) => ({
      label: all.length > 1 ? `${a.label} ${i + 1}` : a.label,
      value: part,
    }))
  );
  const shown = new Set(fields.map(f => f.key).filter(Boolean));
  const extra = task
    ? Object.entries(task).filter(([k, v]) =>
        !shown.has(k) && !HIDDEN_DETAIL_KEY.test(k) &&
        (typeof v === 'string' || typeof v === 'number') && !isBlank(v) && !/^https?:\/\//.test(String(v)))
    : [];
  const visibleFields = fields.filter(f => !isBlank(f.value));

  return createPortal(
    <div className="k-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="k-modal" role="dialog" aria-modal="true" aria-label={title} style={{ maxWidth: '44rem' }}>
        <div className="k-modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.35 }}>{title}</h3>
            {(subtitle || badge) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8 }}>
                {badge && <span className="k-badge-pending">{badge}</span>}
                {subtitle && <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{subtitle}</span>}
              </div>
            )}
          </div>
          <button className="k-modal-close" onClick={onClose} aria-label="Close" title="Close">
            <X size={18} />
          </button>
        </div>

        <div className="k-modal-body">
          {body.filter(b => !isBlank(b.value)).map((b, i) => (
            <div key={i} style={{ marginBottom: 16 }}>
              <p className="k-section-title">{b.label}</p>
              <div className="k-remarks" style={{ whiteSpace: 'pre-wrap' }}>{b.value}</div>
            </div>
          ))}

          {visibleFields.length > 0 && (
            <div className="k-info-grid">
              {visibleFields.map((f, i) => (
                <div key={i} className="k-info-block">
                  <p className="k-field-label">{f.label}</p>
                  <p className="k-field-value">{f.value}</p>
                </div>
              ))}
            </div>
          )}

          {attachments.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p className="k-section-title">Attachments</p>
              {attachmentItems.length === 0 ? (
                <p className="k-field-value" style={{ color: 'var(--text-muted)' }}>No attachment on this ticket.</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {attachmentItems.map((a, i) => /^https?:\/\//i.test(a.value) ? (
                    <a key={i} href={a.value} target="_blank" rel="noopener noreferrer" className="k-info-block"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', color: 'var(--gold-primary)', fontSize: 13, fontWeight: 600 }}>
                      <Paperclip size={14} /> {a.label} <ExternalLink size={12} />
                    </a>
                  ) : (
                    <span key={i} className="k-info-block" style={{ fontSize: 13 }}>
                      <Paperclip size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{a.label}: {a.value}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {extra.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <p className="k-section-title">All details</p>
              <div className="k-info-grid">
                {extra.map(([k, v]) => (
                  <div key={k} className="k-info-block">
                    <p className="k-field-label">{humanizeKey(k)}</p>
                    <p className="k-field-value" style={{ wordBreak: 'break-word' }}>{String(v)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            {onOpenTab && (
              <button className="k-btn-ghost" onClick={() => { onClose(); onOpenTab(); }}>
                Open {tabLabel} tab
              </button>
            )}
            {actions.map((a, i) => isBlank(a.href) ? (
              <button key={i} className="k-btn-gold" disabled title="No link on this row in the sheet" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                {a.label}
              </button>
            ) : (
              <a key={i} className="k-btn-gold" href={a.href} target="_blank" rel="noopener noreferrer"
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ExternalLink size={14} /> {a.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const OpenLink = ({ href, label }) => {
  if (isBlank(href)) {
    return (
      <span title="No link on this row in the sheet" style={{ color: 'var(--text-muted)', opacity: 0.4, display: 'inline-flex' }}>
        <ExternalLink size={18} />
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={label}
      aria-label={label}
      onClick={(e) => e.stopPropagation()}
      style={{ color: 'var(--text-secondary)', display: 'inline-flex', padding: 4, borderRadius: 6 }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gold-primary)'; e.currentTarget.style.background = 'var(--bg-surface-2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'transparent'; }}
    >
      <ExternalLink size={18} />
    </a>
  );
};

// Three distinct states: not loaded (error), empty, or rows.
const Column = ({ title, count, notLoaded, onOpenTab, subheader, children, isEmpty, emptyText = 'No items' }) => (
  <div className="k-card" style={{ display: 'flex', flexDirection: 'column', height: COLUMN_HEIGHT, overflow: 'hidden', minWidth: 0 }}>
    <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</h3>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {!notLoaded && <span className={count > 0 ? 'k-badge-pending' : 'k-badge-completed'}>{count}</span>}
        <button
          onClick={onOpenTab}
          title={`Open the full ${title} tab`}
          aria-label={`Open the full ${title} tab`}
          style={{ display: 'inline-flex', padding: 4, borderRadius: 6, color: 'var(--text-muted)', background: 'transparent', border: 0, cursor: 'pointer' }}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
    {subheader}
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {notLoaded ? (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 20, textAlign: 'center' }}>
          <AlertTriangle size={22} style={{ color: 'var(--c-warning)' }} />
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>{title} data didn't load.</p>
          <button className="k-btn-gold" onClick={onOpenTab}>Open {title} to retry</button>
        </div>
      ) : isEmpty ? (
        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
          {emptyText}
        </div>
      ) : children}
    </div>
  </div>
);

// Clickable when onOpen is given: opens the row's detail modal. Keyboard
// reachable too (Enter / Space), since a div isn't focusable by default.
const Row = ({ children, onOpen }) => (
  <div
    role={onOpen ? 'button' : undefined}
    tabIndex={onOpen ? 0 : undefined}
    onClick={onOpen}
    onKeyDown={onOpen ? (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); }
    } : undefined}
    title={onOpen ? 'View details' : undefined}
    style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', cursor: onOpen ? 'pointer' : undefined, transition: 'background 0.15s ease' }}
    onMouseEnter={onOpen ? (e) => { e.currentTarget.style.background = 'var(--bg-surface-2)'; } : undefined}
    onMouseLeave={onOpen ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}
  >
    {children}
  </div>
);

const HTRow = ({ person, ticketId, text, href, linkLabel, onOpen }) => (
  <Row onOpen={onOpen}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{person || '—'}</span>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, fontFamily: 'monospace' }}>{ticketId}</span>
    </div>
    <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.45, color: 'var(--c-danger)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={text}>
      {text || '—'}
    </p>
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
      <OpenLink href={href} label={linkLabel} />
    </div>
  </Row>
);

const weekSortKey = (w) => {
  const n = parseFloat(w);
  return Number.isNaN(n) ? -Infinity : n;
};

const DoerBoard = ({ currentUser, htData, delegationData, fmsData, loaded, onTabChange }) => {
  const { permissions } = currentUser;
  const me = currentUser.name;
  // FMS drill-down, as in AppSheet: week → FMS group → tasks.
  // Each level is null (showing the list) or 'all' / a chosen value.
  const [fmsWeek, setFmsWeek] = useState(null);
  const [fmsGroup, setFmsGroup] = useState(null);
  // The row whose detail modal is open, as a ready-to-render description.
  const [detail, setDetail] = useState(null);
  const closeDetail = useCallback(() => setDetail(null), []);

  const htDetail = (t, mode) => ({
    title: t.challengeIssue || 'Help ticket',
    subtitle: t.ticketId,
    badge: mode === 'reply' ? 'Reply pending' : 'Resolve pending',
    body: [
      { label: 'Challenge / Issue', value: t.challengeIssue },
      { label: 'Recommendation', value: t.recommendation },
    ],
    fields: [
      { label: 'Raised By', value: t.name },
      { label: 'Delegated To', value: t.issueDelegatedTo },
      { label: 'Department', value: t.department },
      { label: 'Priority', value: t.challengeLevel },
      { label: 'Raised On', value: t.timestamp },
      { label: 'Reply Due', value: t.replyPlanned },
      { label: 'Replied On', value: t.replyActual },
      { label: 'Resolve Due', value: t.resolvePlanned },
      { label: 'Solution 1', value: t.solution1 },
      { label: 'Solution 2', value: t.solution2 },
      { label: 'Solution 3', value: t.solution3 },
      { label: 'Remarks', value: t.remarks },
    ],
    // Always rendered for HT, so "no attachment" is stated rather than implied.
    attachments: [
      { label: 'Attachment', value: t.attachment },
      { label: 'Reply attachment', value: t.resolveAttachment },
    ],
    actions: mode === 'reply'
      ? [{ label: 'Reply', href: t.replyLink || t.newLink || t.problemSolvingLink }]
      : [{ label: 'Resolve', href: t.resolveLink }],
    onOpenTab: () => onTabChange('ht-tasks'),
    tabLabel: 'HT Tasks',
  });

  // Same definitions as the sidebar badge counts in EmployeeDashboard.
  const htReplyPending = useMemo(
    () => htData.filter(t => t.issueDelegatedTo === me && isBlank(t.recommendation)),
    [htData, me]
  );
  const htResolvePending = useMemo(
    () => htData.filter(t => t.name === me && !isBlank(t.resolvePlanned) && isBlank(t.resolveActual)),
    [htData, me]
  );
  const delegationPending = useMemo(
    () => delegationData.filter(t => (t.delegation_status || 'pending').toLowerCase().includes('pending')),
    [delegationData]
  );
  // Every FMS row for this doer, CheckList Task and HelpTicket included —
  // the AppSheet column lists those groups too.
  const fmsTasks = fmsData;
  const fmsWeeks = useMemo(() => {
    const groups = {};
    fmsTasks.forEach(t => {
      const w = String(t.week || '').trim() || 'No week';
      (groups[w] = groups[w] || []).push(t);
    });
    return Object.entries(groups).sort((a, b) => weekSortKey(b[0]) - weekSortKey(a[0]));
  }, [fmsTasks]);

  const fmsWeekTasks = useMemo(
    () => (fmsWeek === 'all' ? fmsTasks : (fmsWeeks.find(([w]) => w === fmsWeek)?.[1] || [])),
    [fmsWeek, fmsTasks, fmsWeeks]
  );
  const fmsGroups = useMemo(() => {
    const groups = {};
    fmsWeekTasks.forEach(t => {
      const g = String(t.fms || '').trim() || 'Other';
      (groups[g] = groups[g] || []).push(t);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [fmsWeekTasks]);
  const fmsVisible = fmsGroup === 'all'
    ? fmsWeekTasks
    : (fmsGroups.find(([g]) => g === fmsGroup)?.[1] || []);

  const columns = [];

  if (permissions.canViewHT) {
    columns.push(
      <Column key="ht-reply" title="HT - Reply Pending" count={htReplyPending.length} notLoaded={!loaded.ht}
        onOpenTab={() => onTabChange('ht-tasks')} isEmpty={htReplyPending.length === 0}>
        {htReplyPending.map(t => (
          <HTRow key={`r-${t.ticketId}-${t.id}`} person={t.name} ticketId={t.ticketId} text={t.challengeIssue}
            href={t.replyLink || t.newLink || t.problemSolvingLink} linkLabel={`Reply to ${t.ticketId}`}
            onOpen={() => setDetail(htDetail(t, 'reply'))} />
        ))}
      </Column>,
      <Column key="ht-resolve" title="HT - Resolve Pending" count={htResolvePending.length} notLoaded={!loaded.ht}
        onOpenTab={() => onTabChange('ht-tasks')} isEmpty={htResolvePending.length === 0}>
        {htResolvePending.map(t => (
          <HTRow key={`s-${t.ticketId}-${t.id}`} person={t.issueDelegatedTo} ticketId={t.ticketId}
            text={t.recommendation || t.challengeIssue}
            href={t.resolveLink} linkLabel={`Resolve ${t.ticketId}`}
            onOpen={() => setDetail(htDetail(t, 'resolve'))} />
        ))}
      </Column>
    );
  }

  if (permissions.canViewDelegation) {
    columns.push(
      <Column key="delegation" title="Delegation" count={delegationPending.length} notLoaded={!loaded.delegation}
        onOpenTab={() => onTabChange('delegation')} isEmpty={delegationPending.length === 0}
        subheader={
          <div style={{ padding: '6px 16px', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border)' }}>
            Pending
          </div>
        }>
        {delegationPending.map((t, i) => {
          const title = t.task || t.title || 'Untitled task';
          return (
            <Row key={`d-${t.did || t.delegation_id || t.id || i}`} onOpen={() => setDetail({
              title,
              subtitle: t.did || t.task_id || t.delegation_id,
              badge: t.delegation_status || 'Pending',
              fields: [
                { key: 'task_created_date', label: 'Created', value: t.task_created_date },
                { key: 'first_date', label: 'First Date', value: t.first_date },
                { key: 'final_date', label: 'Final Date', value: t.final_date },
                { key: 'doer_status', label: 'Your Status', value: t.doer_status || t.doer_response_status },
              ],
              task: Object.fromEntries(Object.entries(t).filter(([k]) => !['task', 'title', 'delegation_status', 'did'].includes(k))),
              actions: [{ label: 'Open submission form', href: t.submission_link }],
              onOpenTab: () => onTabChange('delegation'),
              tabLabel: 'Delegation',
            })}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                <span title={title} style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
                <span style={{ fontSize: 12, color: 'var(--c-warning)', flexShrink: 0 }}>{t.delegation_status || 'Pending'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.final_date || t.task_created_date || ''}</span>
                <OpenLink href={t.submission_link} label="Open submission form" />
              </div>
            </Row>
          );
        })}
      </Column>
    );
  }

  if (permissions.canViewFMS) {
    const weekLabel = fmsWeek === 'all' ? 'All weeks' : `Week ${fmsWeek}`;
    const groupLabel = fmsGroup === 'all' ? 'All groups' : fmsGroup;
    const backBar = fmsWeek !== null && (
      <button
        onClick={() => (fmsGroup !== null ? setFmsGroup(null) : setFmsWeek(null))}
        title={fmsGroup !== null ? `Back to ${weekLabel}` : 'Back to weeks'}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-surface-2)', border: 0, borderBottom: '1px solid var(--border)', cursor: 'pointer', textAlign: 'left', minWidth: 0 }}
      >
        <ChevronLeft size={14} style={{ flexShrink: 0 }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {weekLabel}{fmsGroup !== null ? ` › ${groupLabel}` : ''} · {fmsGroup !== null ? fmsVisible.length : fmsWeekTasks.length}
        </span>
      </button>
    );
    const listRow = (key, label, n, onPick) => (
      <button
        key={key}
        onClick={onPick}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'transparent', border: 0, borderBottom: '1px solid var(--border)', cursor: 'pointer', color: 'var(--text-primary)', textAlign: 'left' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-2)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 600, minWidth: 0 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={label}>{label}</span>
          {n != null && (
            <span style={{ fontSize: 12, padding: '1px 7px', borderRadius: 6, background: 'var(--bg-surface-2)', border: '1px solid var(--border)', color: 'var(--text-secondary)', flexShrink: 0 }}>{n}</span>
          )}
        </span>
        <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
      </button>
    );
    const pickWeek = (w) => { setFmsWeek(w); setFmsGroup(null); };
    columns.push(
      <Column key="fms" title="FMS Task" count={fmsTasks.length} notLoaded={!loaded.fms}
        onOpenTab={() => onTabChange('fms')} isEmpty={fmsTasks.length === 0} subheader={backBar}>
        {fmsWeek === null ? (
          <>
            {listRow('all', 'All', null, () => pickWeek('all'))}
            {fmsWeeks.map(([w, list]) => listRow(w, w, list.length, () => pickWeek(w)))}
          </>
        ) : fmsGroup === null ? (
          fmsGroups.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>No items</div>
          ) : (
            <>
              {listRow('all', 'All', null, () => setFmsGroup('all'))}
              {fmsGroups.map(([g, list]) => listRow(g, g, list.length, () => setFmsGroup(g)))}
            </>
          )
        ) : fmsVisible.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>No items</div>
        ) : fmsVisible.map((t, i) => {
          const delay = parseFloat(t.delay || 0);
          const title = t.what_to_do || t.task_info || t.fms || 'FMS task';
          return (
            <Row key={`f-${t.rowNumber || i}`} onOpen={() => setDetail({
              title,
              subtitle: t.fms,
              badge: delay > 0 ? `${t.delay} delay` : null,
              body: t.task_info && t.task_info !== title ? [{ label: 'Task Info', value: t.task_info }] : [],
              fields: [
                { key: 'planned', label: 'Planned', value: t.planned },
                { key: 'week', label: 'Week', value: t.week },
                { key: 'delay', label: 'Delay', value: t.delay },
              ],
              task: Object.fromEntries(Object.entries(t).filter(([k]) => !['what_to_do', 'task_info', 'fms', 'doer'].includes(k))),
              actions: [{ label: 'Open FMS task', href: t.link }],
              onOpenTab: () => onTabChange('fms'),
              tabLabel: 'FMS',
            })}>
              {fmsGroup === 'all' && (
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.fms}</div>
              )}
              <p title={title} style={{ margin: '3px 0 0', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{title}</p>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, gap: 8 }}>
                <span style={{ fontSize: 12, color: delay > 0 ? 'var(--c-danger)' : 'var(--text-muted)' }}>
                  {t.planned || ''}{delay > 0 ? ` · ${t.delay} delay` : ''}
                </span>
                <OpenLink href={t.link} label="Open FMS task" />
              </div>
            </Row>
          );
        })}
      </Column>
    );
  }

  if (columns.length === 0) {
    return (
      <div className="k-card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
        You don't have access to HT, Delegation or FMS, so the doer board has nothing to show.
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        {columns}
      </div>
      {detail && <DetailModal detail={detail} onClose={closeDetail} />}
    </>
  );
};

export default DoerBoard;
